import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { DataUpload } from '@/common/data-upload';

interface TilesetLocationEditContextValue {
	// The tileset (data upload) currently being positioned on the main map, if any
	editing: DataUpload | null;
	start: (dataUpload: DataUpload) => void;
	stop: () => void;
}

const noop = () => {};

const TilesetLocationEditContext = createContext<TilesetLocationEditContextValue>({
	editing: null,
	start: noop,
	stop: noop,
});

/**
 * Shares which tileset is being repositioned between the data-upload list (which
 * starts editing) and the main map's on-map editor (which renders the gizmo).
 * Provided above both panels in AssetMainOutlet.
 */
export function TilesetLocationEditProvider({ children }: { children: React.ReactNode }) {
	const [editing, setEditing] = useState<DataUpload | null>(null);

	const start = useCallback((dataUpload: DataUpload) => setEditing(dataUpload), []);
	const stop = useCallback(() => setEditing(null), []);

	const value = useMemo(() => ({ editing, start, stop }), [editing, start, stop]);

	return (
		<TilesetLocationEditContext.Provider value={value}>{children}</TilesetLocationEditContext.Provider>
	);
}

export function useTilesetLocationEdit() {
	return useContext(TilesetLocationEditContext);
}
